import { useState, useEffect, useContext, createContext } from 'react';
import jwtDecode from 'jwt-decode';
import api, { authApi } from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const login = async (email, password) => {
    try {
      const response = await authApi.login({ email, password });
      
      // Debug: Log full response structure
      console.log('=== LOGIN RESPONSE DEBUG ===');
      console.log('Full response:', response);
      console.log('response.data:', response.data);
      console.log('response.data.data:', response.data?.data);
      console.log('response.data.data?.token:', response.data?.data?.token);
      console.log('response.data.data?.user:', response.data?.data?.user);
      console.log('==========================');
      
      // Backend returns: { success: true, data: { token: "...", user: {...} } }
      // Try multiple possible locations for the token
      const token = response.data?.data?.token 
                 || response.data?.token 
                 || (response.data?.data && response.data.data.token)
                 || null;
      
      const user = response.data?.data?.user 
                || response.data?.user 
                || (response.data?.data && response.data.data.user)
                || null;

      console.log('Extracted token:', token ? `Found (${token.substring(0, 20)}...)` : 'MISSING');
      console.log('Extracted user:', user ? 'Found' : 'MISSING');
      
      // If still no token, log the full structure
      if (!token) {
        console.error('=== TOKEN NOT FOUND ===');
        console.error('Full response.data:', JSON.stringify(response.data, null, 2));
        console.error('response.data keys:', Object.keys(response.data || {}));
        if (response.data?.data) {
          console.error('response.data.data keys:', Object.keys(response.data.data));
        }
        return { success: false, message: 'Login failed: No token received' };
      }

      if (!user) {
        console.error('No user in response:', response.data);
        return { success: false, message: 'Login failed: No user data received' };
      }

      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);

      return { success: true, user };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      
      // Handle 403 (email not verified) specially
      if (error.response?.status === 403) {
        const errorData = error.response.data;
        return {
          success: false,
          message: errorData?.message || 'Please verify your email address before logging in.',
          requiresVerification: errorData?.requiresVerification || true
        };
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      return { 
        success: false, 
        message: errorMessage
      };
    }
  };

  const signup = async (userData) => {
    try {
      const response = await authApi.signup(userData);
      console.log('Signup response:', response.data);
      
      // Backend returns: { success: true, message: "...", data: { user: {...} } }
      // Note: No token is returned because email verification is required
      const user = response.data.data?.user;

      if (!user) {
        console.error('No user in response:', response.data);
        return { success: false, message: 'Signup failed: No user data received' };
      }

      // Don't set token or user - email verification is required first
      // User will need to verify email and then login
      return { 
        success: true, 
        user,
        message: response.data.message || 'Account created successfully! Please check your email to verify your account.'
      };
    } catch (error) {
      console.error('Signup error:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.message || 'Signup failed';
      return { 
        success: false, 
        message: errorMessage
      };
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (token) {
          const decoded = jwtDecode(token);
          const currentTime = Date.now() / 1000;

          if (decoded.exp > currentTime) {
            // Token is valid, fetch user data
            const response = await authApi.getMe();
            setUser(response.data.data.user);
          } else {
            // Token expired
            logout();
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, loading, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
